import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import auth from "../../fire";
import axiosClient from "../../axiosClient";
import { ClipLoader } from "react-spinners";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";
import { createUserWithEmailAndPassword } from "firebase/auth";

import { Formik, Field } from "formik";
import * as Yup from "yup";
import MessageModal from "../../components/MessageModal/MessageModal";

interface SignUpValues {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
}

const SignUp = () => {
  const navigate = useNavigate();
  //const [user, loading, error] = useAuthState(auth);
  const [showPassword, setShowPassword] = useState(false);
  const { search } = useLocation();
  const urlParams = new URLSearchParams(search);
  const urlEmail = urlParams.get("email");
  const [isUninvitedMessageModalOpen, setIsUninvitedMessageModalOpen] =
    useState(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);

  const SignupSchema = Yup.object().shape({
    firstName: Yup.string()
      .min(2, "First name too short!")
      .max(50, "First name too long!")
      .required("First name required"),
    lastName: Yup.string()
      .min(2, "Last name too short!")
      .max(50, "Last name too long!")
      .required("Last name required"),
    email: Yup.string().email("Invalid email").required("Email required"),
    password: Yup.string()
      .min(7, "Password must be atleast 7 characters!")
      .max(50, "Password too long!")
      .required("Password required")
      .matches(/(?=.*[0-9])/, "Password must contain at least 1 number"),
  });

  const showPasswordHandler = () => {
    if (!showPassword) {
      setShowPassword(true);
    } else {
      setShowPassword(false);
    }
  };

  const checkIfEmailInvited = async (email: string): Promise<boolean> => {
    try {
      const res = await axiosClient.get(
        `/api/users/check-invite?email=${email}`,
      );
      return res.status === 200;
    } catch (err) {
      return false;
    }
  };

  return (
    <>
      <div className="flex flex-col py-4 px-4">
        <div className="w-full mb-14">
          <div>
            <h1 className="mb-1 mt-1 font-bold leading-7 text-indigo-600 text-4xl tracking-tight">
              Superset
            </h1>
            <span className="font-normal text-med tracking-[1.4px] text-indigo-600">
              Workout Tracker
            </span>
            <h2 className="mt-6 text-2xl font-bold tracking-tight text-gray-900">
              Create your account
            </h2>
          </div>
          <Formik<SignUpValues>
            initialValues={{
              firstName: "",
              lastName: "",
              email: urlEmail ? urlEmail : "",
              password: "",
            }}
            validationSchema={SignupSchema}
            onSubmit={async (values) => {
              const email = values.email;
              try {
                const emailInvited = await checkIfEmailInvited(email);
                if (emailInvited) {
                  await createUserWithEmailAndPassword(
                    auth,
                    email,
                    values.password,
                  );

                  const newUser = {
                    email: values.email,
                    firstName: values.firstName,
                    lastName: values.lastName,
                  };
                  await axiosClient.post("/api/users/", newUser);

                  // Fire-and-forget: send a branded verification email. Signup
                  // success shouldn't depend on the email going out.
                  axiosClient
                    .post("/api/users/send-verification")
                    .catch((err) => {
                      console.error("Failed to send verification email:", err);
                    });

                  setIsSuccessModalOpen(true);
                } else {
                  setIsUninvitedMessageModalOpen(true);
                }
              } catch (error) {
                console.log("Error signing user up:", error);
                setIsErrorModalOpen(true);
              }
            }}
          >
            {({ errors, touched, isSubmitting, handleSubmit }) => (
              <form
                onSubmit={handleSubmit}
                className="mt-6 flex flex-col gap-y-2"
              >
                <div>
                  <label htmlFor="firstName" className="sr-only">
                    First name
                  </label>
                  <Field
                    id="firstName"
                    name="firstName"
                    className="relative block w-full appearance-none rounded-md border shadow-sm border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-500 focus:z-10 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 text-sm"
                    placeholder="First name"
                  />
                  <div className="min-h-[1.25rem]">
                    {errors.firstName && touched.firstName ? (
                      <div className="text-sm text-red-600">
                        *{errors.firstName}
                      </div>
                    ) : null}
                  </div>
                </div>

                <div>
                  <label htmlFor="lastName" className="sr-only">
                    Last name
                  </label>
                  <Field
                    id="lastName"
                    name="lastName"
                    className="relative block w-full appearance-none rounded-md shadow-sm border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-500 focus:z-10 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 text-sm"
                    placeholder="Last name"
                  />
                  <div className="min-h-[1.25rem]">
                    {errors.lastName && touched.lastName ? (
                      <div className="text-sm text-red-600">
                        *{errors.lastName}
                      </div>
                    ) : null}
                  </div>
                </div>

                <div>
                  <label htmlFor="emailAddress" className="sr-only">
                    Email address
                  </label>
                  <Field
                    id="emailAddress"
                    name="email"
                    type="email"
                    className="relative block w-full appearance-none rounded-md shadow-sm border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-500 focus:z-10 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 text-sm"
                    placeholder="Email address"
                  />
                  <div className="min-h-[1.25rem]">
                    {errors.email && touched.email ? (
                      <div className="text-sm text-red-600">*{errors.email}</div>
                    ) : null}
                  </div>
                </div>

                <div className="relative">
                  <label htmlFor="password" className="sr-only">
                    Password
                  </label>
                  <Field
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    className="relative block w-full appearance-none rounded-md shadow-sm border border-gray-300 px-3 py-2 pr-10 text-gray-900 placeholder-gray-500 focus:z-10 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 text-sm"
                    placeholder="Password"
                  />
                  <button
                    className="absolute top-[7px] right-2 z-10"
                    type="button"
                    onClick={showPasswordHandler}
                  >
                    {!showPassword && (
                      <EyeIcon
                        className="w-6 h-auto text-indigo-600"
                        aria-hidden="true"
                      ></EyeIcon>
                    )}
                    {showPassword && (
                      <EyeSlashIcon
                        className="w-6 h-auto text-indigo-600"
                        aria-hidden="true"
                      ></EyeSlashIcon>
                    )}
                  </button>
                  <div className="min-h-[1.25rem]">
                    {errors.password && touched.password ? (
                      <div className="text-sm text-red-600">
                        *{errors.password}
                      </div>
                    ) : null}
                  </div>
                </div>

                <div>
                  <button
                    type="submit"
                    className="mt-1 group relative flex w-full justify-center rounded-md border border-transparent bg-indigo-600 py-2 px-4 text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:pointer-events-none"
                    disabled={isSubmitting}
                  >
                    {!isSubmitting && `Sign up`}
                    {isSubmitting && (
                      <ClipLoader
                        color="rgb(white)"
                        speedMultiplier={0.6}
                        size={"1.25rem"}
                      />
                    )}
                  </button>
                </div>
              </form>
            )}
          </Formik>
        </div>
      </div>
      <div className=" px-4 py-4 w-full flex justify-center fixed bottom-0 bg-gray-50">
        <p className="text-sm text-gray-900">
          Already have an account?{" "}
          <Link to="/login" className="text-indigo-600 font-medium">
            Sign in instead
          </Link>
        </p>
      </div>
      <MessageModal
        isOpen={isUninvitedMessageModalOpen}
        onModalClose={() => {
          setIsUninvitedMessageModalOpen(false);
        }}
        iconType={"locked"}
        title={"Email Not On Invite List"}
        message={
          "Sorry, this app is currently in private beta. Only invited users can create an account."
        }
      />
      <MessageModal
        isOpen={isErrorModalOpen}
        onModalClose={() => {
          setIsErrorModalOpen(false);
        }}
        iconType={"error"}
        title={"Error Signing Up"}
        message={
          "Sorry, something went wrong trying to sign you up.\nPlease try again."
        }
      />
      <MessageModal
        isOpen={isSuccessModalOpen}
        onModalClose={() => {
          setIsSuccessModalOpen(false);
          navigate("/");
        }}
        iconType={"fire"}
        title={"Welcome to Workout Tracker!"}
        message={"Sign up successful!\nYou are now logged in."}
      />
    </>
  );
};

export default SignUp;
